"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="234083" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-lg" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default font-medium text-white relative py-2 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-[#A4D41D] after:transition-all after:duration-300 hover:after:w-full hover:text-[#A4D41D] transition-colors tracking-tight">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-white">
          <Menu className="size-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-black border-l border-white/10">
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
        <div className="flex flex-col gap-8 mt-8">
          <Link to="/" onClick={() => setIsOpen(false)}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645714/img/FullLogo_Transparent_NoBuffer (1).png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645714/img/FullLogo_Transparent_NoBuffer (1).png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645714/img/FullLogo_Transparent_NoBuffer (1).png-wvc-sizes" alt="VOLTEX Logo" className="h-10 w-auto" loading="lazy" />
          </Link>
          {loading ? <div className="flex flex-col gap-4">
              {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded-lg" />)}
            </div> : <nav className="flex flex-col gap-6">
              {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="font-default font-bold text-lg text-white hover:text-[#A4D41D] transition-colors tracking-tight" onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>)}
              <Button asChild className="rounded-lg bg-primary text-primary-foreground w-full mt-4 shadow-lg hover:bg-primary/90 animate-pulse">
                <Link to="/contact" onClick={() => setIsOpen(false)}>Request Service</Link>
              </Button>
            </nav>}
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-black border-b border-transparent"}`}>
      <header className="container mx-auto px-4 h-20 flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645714/img/FullLogo_Transparent_NoBuffer (1).png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645714/img/FullLogo_Transparent_NoBuffer (1).png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645714/img/FullLogo_Transparent_NoBuffer (1).png-wvc-sizes" alt="VOLTEX Logo" className="h-12 md:h-14 w-auto" loading="lazy" />
          </Link>
        </div>

        <div className="flex items-center gap-8">
          <MenuProvider menu_id="32">
            <DesktopNav />
          </MenuProvider>

          <Button asChild className="hidden md:inline-flex rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl font-default font-bold tracking-tighter transition-all duration-300 hover:scale-105">
            <Link to="/contact">Request Service</Link>
          </Button>

          <MenuProvider menu_id="32">
            <MobileNav />
          </MenuProvider>
        </div>
      </header>
    </section>;
}